import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';
import type { ConfigSetting, MoneyJson } from '@/features/products/types/products.types';
import { productKeys } from '@/features/products/hooks/useProductsQuery';

const smrPath = '/smr';

type SettingValue = string | number | boolean | string[] | MoneyJson;

interface ApplyCatalogVars {
    group?: string;
    defaults: Record<string, SettingValue>;
}

interface ApplyCatalogResult {
    created_count: number;
    skipped_count: number;
    skipped_keys: string[];
    data: ConfigSetting[];
}

interface UpdateByKeyVars {
    productId: string;
    key: string;
    value: SettingValue;
    config_option_id?: string | null;
}

interface DeleteByKeyVars {
    productId: string;
    key: string;
}

interface UpdateByIdVars {
    productId: string;
    settingId: string;
    value: SettingValue;
}

interface DeleteByIdVars {
    productId: string;
    settingId: string;
}

interface CreateSettingVars {
    productId: string;
    key: string;
    value: SettingValue;
    config_option_id?: string | null;
}

export const useConfigSettingMutations = (productId: string) => {
    const queryClient = useQueryClient();

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });

    const updateMutation = useMutation({
        // Keys are user-controlled (custom-add form) and may contain `/`, `?`, `#`,
        // spaces, etc. — encode the path segment to keep the URL well-formed.
        mutationFn: ({ key, value, config_option_id }: Omit<UpdateByKeyVars, 'productId'>) =>
            api.put<{ data: ConfigSetting }>(
                `${smrPath}/products/${productId}/config-settings/${encodeURIComponent(key)}`,
                { value, config_option_id },
            ).then(r => r.data.data),
        onSuccess: invalidate,
    });

    const deleteMutation = useMutation({
        mutationFn: ({ key }: Omit<DeleteByKeyVars, 'productId'>) =>
            api.delete(`${smrPath}/products/${productId}/config-settings/${encodeURIComponent(key)}`),
        onSuccess: invalidate,
    });

    const updateByIdMutation = useMutation({
        mutationFn: ({ settingId, value }: Omit<UpdateByIdVars, 'productId'>) =>
            api.put<{ data: ConfigSetting }>(
                `${smrPath}/products/${productId}/config-settings/by-id/${settingId}`,
                { value },
            ).then(r => r.data.data),
        onSuccess: invalidate,
    });

    const deleteByIdMutation = useMutation({
        mutationFn: ({ settingId }: Omit<DeleteByIdVars, 'productId'>) =>
            api.delete(`${smrPath}/products/${productId}/config-settings/by-id/${settingId}`),
        onSuccess: invalidate,
    });

    const createMutation = useMutation({
        mutationFn: ({ key, value, config_option_id }: Omit<CreateSettingVars, 'productId'>) =>
            api.post(
                `${smrPath}/products/${productId}/config-settings`,
                { settings: [{ key, value, config_option_id }] },
            ),
        onSuccess: invalidate,
    });

    const applyCatalogMutation = useMutation({
        mutationFn: ({ group, defaults }: ApplyCatalogVars) =>
            api.post<{ data: ApplyCatalogResult }>(
                `${smrPath}/products/${productId}/config-settings/apply-catalog`,
                { group, defaults },
            ).then(r => r.data.data),
        onSuccess: invalidate,
    });

    return {
        updateMutation,
        deleteMutation,
        updateByIdMutation,
        deleteByIdMutation,
        createMutation,
        applyCatalogMutation,
    };
};
